<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useTemplateRefsList } from '@vueuse/core';

import api from '@/api';
import { RelationshipStatus } from '@/helpers/const';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';

import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
import ScenarioTypeTabs from './modules/ScenarioTypeTabs.vue';
import SimpleExpensesForm from './modules/Expenses/SimpleExpensesForm.vue';

import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import type { Applicant, ApplicantId } from '@/types';

const { loanApp, loanApplicants, sectionStates, saveAndGoNext, goBack } = useUserQuoteProcess();

const isReady = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);

const expenseSectionType = computed(() => sectionStates.value.expenses);

const applicantsList = computed(() => loanApp.value.applicants);

/** Partner index in `loanApp.applicants` when this applicant is married/defacto to another applicant on the loan. */
const getMarriedPartnerApplicantIndex = (selfIndex: number, applicants: Applicant[]): number | null => {
  const a = applicants[selfIndex];
  if (
    a?.relationshipStatus === RelationshipStatus.MARRIED_OR_DEFACTO &&
    a.relatedApplicantIndex !== undefined &&
    a.relatedApplicantIndex >= 0 &&
    a.relatedApplicantIndex < applicants.length
  ) {
    return a.relatedApplicantIndex;
  }
  const reciprocal = applicants.findIndex(
    (other) =>
      other.relationshipStatus === RelationshipStatus.MARRIED_OR_DEFACTO && other.relatedApplicantIndex === selfIndex,
  );
  return reciprocal >= 0 ? reciprocal : null;
};

const hasMarriedCoupleOnLoan = computed(() =>
  applicantsList.value.some((_, index) => getMarriedPartnerApplicantIndex(index, applicantsList.value) !== null),
);

/**
 * Applicant ids for this step.
 * Simple: one id per household (married/defacto pair shares a single card).
 * Comprehensive: every applicant, with spouses listed consecutively (lower index first within each pair).
 */
const validApplicants = computed(() => {
  const applicants = applicantsList.value;
  const processed = new Set<number>();
  const ordered: ApplicantId[] = [];

  if (expenseSectionType.value === 'simple') {
    applicants.forEach((a, index) => {
      if (processed.has(index)) return;
      processed.add(index);
      const partnerIdx = getMarriedPartnerApplicantIndex(index, applicants);
      if (partnerIdx !== null) {
        processed.add(partnerIdx);
      }
      ordered.push(a.id as ApplicantId);
    });
    return ordered;
  }

  applicants.forEach((_, index) => {
    if (processed.has(index)) return;
    const partnerIdx = getMarriedPartnerApplicantIndex(index, applicants);
    if (partnerIdx !== null) {
      const i = Math.min(index, partnerIdx);
      const j = Math.max(index, partnerIdx);
      ordered.push(applicants[i].id as ApplicantId, applicants[j].id as ApplicantId);
      processed.add(i);
      processed.add(j);
    } else {
      ordered.push(applicants[index].id as ApplicantId);
      processed.add(index);
    }
  });

  return ordered;
});

const applicantIndexInLoanApp = (applicantId: ApplicantId) =>
  applicantsList.value.findIndex((a) => a.id === applicantId);

const financials = ref(cloneDeep(loanApp.value.financials));
const comprehensiveExpensesFormsRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();
const simpleExpensesFormsRefs = useTemplateRefsList<InstanceType<typeof SimpleExpensesForm>>();
const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 ? validApplicants.value[0] : undefined,
);

const isCompleted = (id: ApplicantId) => financials.value[id]?.expenses?.numberOfDependants !== undefined;

const validateExpenses = async () => {
  let isValid = true;

  if (expenseSectionType.value === 'simple') {
    const refCount = simpleExpensesFormsRefs.value.length;
    if (refCount > 0) {
      for (let i = 0; i < refCount; i++) {
        const formIsValid = (await simpleExpensesFormsRefs.value[i].update()) ?? false;
        if (!formIsValid) {
          isValid = false;
        }
      }
      if (!isValid) {
        window.scrollTo(0, 0);
      }
    } else {
      isValid = false;
    }
  } else {
    const refCount = comprehensiveExpensesFormsRefs.value.length;
    for (let i = 0; i < refCount; i++) {
      const formInstance = comprehensiveExpensesFormsRefs.value[i];
      if (formInstance) {
        isValid = (await formInstance.update()) ?? false;
        if (!isValid) {
          activeTab.value = validApplicants.value[i];
          window.scrollTo(0, 0);
          break;
        }
      } else {
        isValid = false;
        break;
      }
    }
  }
  return isValid;
};

/** First partner name found (for simple combined-expenses copy). */
const marriedPartnerExpenseHintName = computed(() => {
  const applicants = applicantsList.value;
  for (let i = 0; i < applicants.length; i++) {
    const p = getMarriedPartnerApplicantIndex(i, applicants);
    if (p !== null) {
      const partner = applicants[p];
      return partner.firstName || `Applicant ${p + 1}`;
    }
  }
  return '';
});

const displayName = (applicant: Applicant, applicantId: ApplicantId) => {
  const applicants = applicantsList.value;
  const selfIndex = applicantIndexInLoanApp(applicantId);
  if (selfIndex < 0) {
    if (applicant?.firstName) return applicant.firstName;
    return 'Applicant';
  }
  const partnerIdx = getMarriedPartnerApplicantIndex(selfIndex, applicants);
  if (partnerIdx !== null) {
    const i = Math.min(selfIndex, partnerIdx);
    const j = Math.max(selfIndex, partnerIdx);
    const left = applicants[i].firstName || `Applicant ${i + 1}`;
    const right = applicants[j].firstName || `Applicant ${j + 1}`;
    return `${left} & ${right}`;
  }
  if (applicant.firstName) return applicant.firstName;
  return `Applicant ${selfIndex + 1}`;
};

const saveAndContinue = async () => {
  if (await validateExpenses()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};

onBeforeMount(async () => {
  isReady.value = false;
  try {
    const expenseTypesResponse = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    if (expenseTypesResponse.expenseTypes?.length) {
      expenseTypesResponse.expenseTypes.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      expenseTypes.value = expenseTypesResponse.expenseTypes;
    }
  } finally {
    isReady.value = true;
  }
});

watch(
  expenseSectionType,
  (newType, oldType) => {
    const isRelevantChange =
      (newType === 'simple' && oldType === 'comprehensive') || (newType === 'comprehensive' && oldType === 'simple');

    if (!isRelevantChange) return;

    for (const a of applicantsList.value) {
      const id = a.id as ApplicantId;
      if (financials.value[id]) {
        financials.value[id].expenses = null;
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="mb-4 pb-2">
    <h2 class="text-2xl font-medium">
      Enter
      <BxHighlight>expenses</BxHighlight>
      for each household
    </h2>
  </div>
  <div v-if="isReady" class="section-panel md:p-6">
    <div class="mb-6">
      <ScenarioTypeTabs :section-key="'expenses'" title="Expenses" />
    </div>
    <div v-if="expenseSectionType === 'simple' && hasMarriedCoupleOnLoan && marriedPartnerExpenseHintName" class="my-6">
      <p>
        Please include your combined household expenses including
        {{ marriedPartnerExpenseHintName }}.
      </p>
    </div>
    <div class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <div v-if="expenseSectionType === 'simple'">
          <BxCard>
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customerExpense" /></span>
              <span>{{ displayName(loanApplicants[applicantId], applicantId) }}</span>
            </h3>
            <SimpleExpensesForm
              :ref="simpleExpensesFormsRefs.set"
              v-model="financials[applicantId].expenses"
              :applicant-id="applicantId"
              :expense-types="expenseTypes"
            />
          </BxCard>
        </div>
        <div v-else>
          <BxCard :class="applicantId !== activeTab && 'hidden'">
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customerExpense" /></span>
              <span>{{ displayName(loanApplicants[applicantId], applicantId) }}</span>
            </h3>
            <ExpensesForm
              :ref="comprehensiveExpensesFormsRefs.set"
              v-model="financials[applicantId].expenses"
              :applicant-id="applicantId"
              :animate="applicantId === activeTab"
              :expense-types="expenseTypes"
              :show-buttons="validApplicants.length > 1"
              @save="activeTab = undefined"
              @cancel="activeTab = undefined"
            />
          </BxCard>
          <BxCard :class="applicantId === activeTab && 'hidden'">
            <div class="flex items-center">
              <div class="card-details">
                <div class="font-medium">
                  <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
                    <span class="mr-3 hidden md:block"><BxIcon name="customerExpense" /></span>
                    <span>{{ displayName(loanApplicants[applicantId], applicantId) }}</span>
                  </h3>
                  <span class="ml-6 font-normal text-gray-400"
                    >({{ isCompleted(applicantId) ? 'completed' : 'incomplete' }})</span
                  >
                </div>
              </div>
              <div class="card-button">
                <BxButton
                  variant="secondary"
                  :disabled="activeTab && activeTab !== applicantId"
                  @click="activeTab = applicantId"
                >
                  {{ isCompleted(applicantId) ? 'Edit' : 'Add' }} expenses
                </BxButton>
              </div>
            </div>
          </BxCard>
        </div>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-details {
  flex-grow: 1;
}

.card-button {
  flex-basis: 200px;

  text-align: right;
}
</style>
